import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { Album, Photo } from '../services/photoService';
import { fetchAlbumsFromApiWithETag, fetchAlbumsFromApi, fetchAlbumById } from '../services/api/albums';
import { cacheGet, cacheSet, cacheInvalidateByPrefix } from '../services/cache';
import { cacheKeys } from '../services/cache/cacheKeys';
import { hasFreshSasUrls } from '../services/sasTokenUtils';

// Mutation signal: describes a single granular album change.
// useAlbumList watches this to sync changes into its local state.
export type AlbumMutation =
  | { type: 'upsert'; album: Album }
  | { type: 'prepend'; album: Album }
  | { type: 'remove'; albumId: string }
  | { type: 'patch'; albumId: string; updates: Partial<Album> };

interface PhotoCacheContextType {
  // Albums cache
  albums: Album[];
  albumsLoaded: boolean;
  loadingAlbums: boolean;
  getAlbums: (orderBy?: string, orderDirection?: string) => Promise<Album[]>;
  invalidateAlbumCache: () => void;

  /**
   * Bulk-replace the in-memory album list. Used by `useAlbumList` to keep the
   * shared cache warm after a successful full-list fetch, so that navigating
   * away and back to the home page can hydrate instantly without re-fetching.
   */
  setAlbumsBulk: (albums: Album[]) => void;

  // Granular album mutators (used by the event dispatcher framework)
  upsertAlbum: (album: Album) => void;
  prependAlbum: (album: Album) => void;
  removeAlbum: (albumId: string) => void;
  patchAlbum: (albumId: string, updates: Partial<Album>) => void;

  /** Last mutation signal — useAlbumList watches this to apply partial updates. */
  lastAlbumMutation: AlbumMutation | null;

  /**
   * Refetch a single album from the API and patch its cached entry with the
   * fresh DTO (including freshly-signed `thumbnailUrl` / `mediumUrl`).
   * Used by the album thumbnail `<img>` to recover from 403s caused by an
   * expired SAS token. Concurrent calls for the same album are coalesced.
   * Returns the refreshed album, or `null` if the refetch failed.
   */
  refreshAlbum: (albumId: string) => Promise<Album | null>;

  // Photos cache
  photosByAlbum: Record<string, Photo[]>;
  getPhotosForAlbum: (albumId: string) => Photo[] | undefined;
  setPhotosForAlbum: (albumId: string, photos: Photo[], etag?: string | null) => void;
  invalidatePhotoCache: (albumId?: string) => void;
  updatePhotoInCache: (albumId: string, photoId: string, updatedPhoto: Photo) => void;
}

const PhotoCacheContext = createContext<PhotoCacheContextType | undefined>(undefined);

export const PhotoCacheProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [albumsLoaded, setAlbumsLoaded] = useState<boolean>(false);
  const [loadingAlbums, setLoadingAlbums] = useState<boolean>(false);
  const [photosByAlbum, setPhotosByAlbum] = useState<Record<string, Photo[]>>({});
  const [lastAlbumMutation, setLastAlbumMutation] = useState<AlbumMutation | null>(null);

  // Track in-flight getAlbums request to prevent duplicates
  const getAlbumsPromiseRef = useRef<Promise<Album[]> | null>(null);

  // Track in-flight refreshAlbum requests so concurrent thumbnail 403s for
  // the same album coalesce into a single network call.
  const refreshAlbumPromisesRef = useRef<Map<string, Promise<Album | null>>>(new Map());

  // Bulk-replace the cached album list. Used by `useAlbumList` to push its
  // fully-fetched album list into the shared cache so other consumers (e.g.
  // the album detail page's `useAlbumData`) see the same data without re-
  // fetching, and so re-mounting `HomePage` can hydrate synchronously.
  const setAlbumsBulk = useCallback((next: Album[]) => {
    setAlbums(next);
    setAlbumsLoaded(true);
  }, []);

  // ── Granular album mutators ──
  // These are used by the event dispatcher framework to apply partial updates
  // without resetting the albumsLoaded flag or scroll position.

  // Insert or replace a single album by ID
  const upsertAlbum = useCallback((album: Album) => {
    setAlbums(prev => {
      const idx = prev.findIndex(a => a.id === album.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = album;
        return updated;
      }
      return [...prev, album];
    });
    setLastAlbumMutation({ type: 'upsert', album });
  }, []);

  // Add a new album at the beginning (skip if already present)
  const prependAlbum = useCallback((album: Album) => {
    setAlbums(prev => {
      if (prev.some(a => a.id === album.id)) return prev;
      return [album, ...prev];
    });
    setLastAlbumMutation({ type: 'prepend', album });
  }, []);

  // Remove an album by ID (no-op if not found)
  const removeAlbum = useCallback((albumId: string) => {
    setAlbums(prev => {
      const filtered = prev.filter(a => a.id !== albumId);
      if (filtered.length === prev.length) return prev; // no-op
      return filtered;
    });
    setLastAlbumMutation({ type: 'remove', albumId });
  }, []);

  // Merge partial fields into an existing album entry
  const patchAlbum = useCallback((albumId: string, updates: Partial<Album>) => {
    setAlbums(prev => {
      const idx = prev.findIndex(a => a.id === albumId);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ...updates };
      return updated;
    });
    setLastAlbumMutation({ type: 'patch', albumId, updates });
  }, []);

  // Get albums with caching — loads from IndexedDB first, then revalidates from API
  const getAlbums = useCallback(async (orderBy?: string, orderDirection?: string) => {
    // If already loaded in memory, return immediately
    if (albumsLoaded && albums.length > 0) {
      return albums;
    }

    // Deduplicate concurrent calls
    if (getAlbumsPromiseRef.current) {
      return getAlbumsPromiseRef.current;
    }

    const promise = (async () => {
      try {
        setLoadingAlbums(true);

        // Try to load from IndexedDB first for instant display, capturing the
        // cached ETag so we can revalidate against the server with If-None-Match.
        // If any cached SAS URL has expired (or is about to), drop the ETag so
        // the server returns a fresh 200 with new tokens instead of a 304 that
        // would preserve dead URLs — but still paint the cached data first so
        // the UI shows something instantly. The background revalidation will
        // replace the URLs within milliseconds, and the `<img>` 403→SAS-refresh
        // path handles any stragglers.
        const cacheKey = cacheKeys.albums({ orderBy, orderDirection });
        let cachedEtag: string | null = null;
        try {
          const cached = await cacheGet<Album[]>(cacheKey);
          if (cached && cached.data.length > 0) {
            setAlbums(cached.data);
            setAlbumsLoaded(true);
            cachedEtag = hasFreshSasUrls(cached.data) ? cached.etag : null;
          }
        } catch {
          // Cache read failed — continue with API fetch
        }

        // Revalidate against the API. A 304 means our cached data is still
        // good; only update local state if the server returned fresh data.
        // First page only — pagination loop below handles the rest.
        const PAGE_SIZE = 100;
        const response = await fetchAlbumsFromApiWithETag(
          { orderBy, orderDirection, skip: 0, take: PAGE_SIZE },
          cachedEtag
        );

        if (response.notModified) {
          // Cached data is still fresh — keep current state. Refresh timestamp
          // by re-saving the cached data so TTL resets.
          if (albums.length > 0) {
            cacheSet(cacheKey, albums, response.etag ?? cachedEtag).catch(() => {});
          }
          return albums;
        }

        if (response.data) {
          let accumulated = response.data.items;
          const totalCount = response.data.totalCount;

          // Page through any remaining albums. Backend caps `take` at 100, so
          // looping is required for users with more than 100 visible albums.
          while (accumulated.length < totalCount) {
            const next = await fetchAlbumsFromApi({
              orderBy,
              orderDirection,
              skip: accumulated.length,
              take: PAGE_SIZE,
            });
            if (next.items.length === 0) break;
            const existingIds = new Set(accumulated.map(a => a.id));
            const newItems = next.items.filter(a => !existingIds.has(a.id));
            if (newItems.length === 0) break;
            accumulated = [...accumulated, ...newItems];
          }

          setAlbums(accumulated);
          setAlbumsLoaded(true);
          // Store in IndexedDB. ETag from the first page only applies to a
          // page-1 response, so persist without an ETag when we paginated.
          const etagToStore = accumulated.length === response.data.items.length
            ? response.etag
            : null;
          cacheSet(cacheKey, accumulated, etagToStore).catch(() => {});
          return accumulated;
        }

        return albums;
      } catch (error) {
        // If we have cached data, return it despite the error
        if (albumsLoaded && albums.length > 0) {
          return albums;
        }
        throw error;
      } finally {
        setLoadingAlbums(false);
        getAlbumsPromiseRef.current = null;
      }
    })();

    getAlbumsPromiseRef.current = promise;
    return promise;
  }, [albumsLoaded, albums]);

  // Invalidate album cache
  const invalidateAlbumCache = useCallback(() => {
    setAlbums([]);
    setAlbumsLoaded(false);
    cacheInvalidateByPrefix('albums').catch(() => {});
  }, []);

  // Refetch a single album and patch the cached entry with fresh SAS URLs.
  // Used by AlbumThumbnail on 403 to recover from expired tokens.
  const refreshAlbum = useCallback((albumId: string): Promise<Album | null> => {
    const inFlight = refreshAlbumPromisesRef.current.get(albumId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      try {
        const fresh = await fetchAlbumById(albumId);
        setAlbums(prev => {
          const idx = prev.findIndex(a => a.id === fresh.id);
          if (idx < 0) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...fresh };
          return updated;
        });
        setLastAlbumMutation({
          type: 'patch',
          albumId: fresh.id,
          updates: {
            thumbnailUrl: fresh.thumbnailUrl,
            mediumUrl: fresh.mediumUrl,
          },
        });
        return fresh;
      } catch {
        return null;
      } finally {
        refreshAlbumPromisesRef.current.delete(albumId);
      }
    })();

    refreshAlbumPromisesRef.current.set(albumId, promise);
    return promise;
  }, []);

  // Get photos for a specific album from cache
  const getPhotosForAlbum = useCallback((albumId: string) => {
    return photosByAlbum[albumId];
  }, [photosByAlbum]);

  // Set photos for a specific album in cache (memory + IndexedDB).
  // Caller may pass the ETag returned by the API so future requests can use
  // If-None-Match for cheap revalidation.
  const setPhotosForAlbum = useCallback((albumId: string, photos: Photo[], etag: string | null = null) => {
    setPhotosByAlbum(prev => ({
      ...prev,
      [albumId]: photos
    }));
    // Persist to IndexedDB (fire and forget)
    cacheSet(cacheKeys.albumPhotos(albumId), photos, etag).catch(() => {});
  }, []);

  // Invalidate photo cache for a specific album or all albums
  const invalidatePhotoCache = useCallback((albumId?: string) => {
    if (albumId) {
      setPhotosByAlbum(prev => {
        const updated = { ...prev };
        delete updated[albumId];
        return updated;
      });
      cacheInvalidateByPrefix(cacheKeys.albumPhotos(albumId)).catch(() => {});
    } else {
      setPhotosByAlbum({});
      cacheInvalidateByPrefix('album-photos').catch(() => {});
    }
  }, []);

  // Update a single photo's URLs in the cache (e.g. after SAS token refresh)
  const updatePhotoInCache = useCallback((albumId: string, photoId: string, updatedPhoto: Photo) => {
    setPhotosByAlbum(prev => {
      const existing = prev[albumId];
      if (!existing) return prev;
      const updated = existing.map(p => p.id === photoId ? updatedPhoto : p);
      return { ...prev, [albumId]: updated };
    });
  }, []);

  const value = {
    albums,
    albumsLoaded,
    loadingAlbums,
    getAlbums,
    invalidateAlbumCache,
    setAlbumsBulk,
    upsertAlbum,
    prependAlbum,
    removeAlbum,
    patchAlbum,
    lastAlbumMutation,
    refreshAlbum,
    photosByAlbum,
    getPhotosForAlbum,
    setPhotosForAlbum,
    invalidatePhotoCache,
    updatePhotoInCache
  };

  return (
    <PhotoCacheContext.Provider value={value}>
      {children}
    </PhotoCacheContext.Provider>
  );
};

export const usePhotoCache = () => {
  const context = useContext(PhotoCacheContext);
  if (context === undefined) {
    throw new Error('usePhotoCache must be used within a PhotoCacheProvider');
  }
  return context;
};

/**
 * Like {@link usePhotoCache}, but returns a no-op shim when used outside a
 * `PhotoCacheProvider`. Useful for components (e.g. `PhotoGrid`) that benefit
 * from the cache when present but should still render in isolated tests or
 * preview surfaces that don't set up the provider.
 */
export const usePhotoCacheOptional = (): PhotoCacheContextType => {
  const context = useContext(PhotoCacheContext);
  return context ?? noopPhotoCache;
};

const noopPhotoCache: PhotoCacheContextType = {
  albums: [],
  albumsLoaded: false,
  loadingAlbums: false,
  getAlbums: async () => [],
  invalidateAlbumCache: () => {},
  setAlbumsBulk: () => {},
  upsertAlbum: () => {},
  prependAlbum: () => {},
  removeAlbum: () => {},
  patchAlbum: () => {},
  lastAlbumMutation: null,
  refreshAlbum: async () => null,
  photosByAlbum: {},
  getPhotosForAlbum: () => undefined,
  setPhotosForAlbum: () => {},
  invalidatePhotoCache: () => {},
  updatePhotoInCache: () => {},
};
